import { useEffect, useMemo, useRef, useState } from 'react'
import { DataGrid, DataGridProps, GridColDef } from '@mui/x-data-grid'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'

const ACTIONS_FIELD = 'acciones'
const STICKY_CLASS = 'sticky-actions'
const HEADER_OFFSET_VAR = '--actions-header-offset'

// Render every column so the actions cell exists even when it is off-screen
// (DataGrid v6 virtualizes columns with a default buffer of 3).
const COLUMN_BUFFER = 50

const stickyShadow = '-4px 0 6px -4px rgba(0, 0, 0, 0.25)'

const gridSx = {
    // Styling ::-webkit-scrollbar makes Chrome/Safari/Edge on macOS draw a classic
    // scrollbar instead of the overlay one hidden by "Show scroll bars: when scrolling".
    '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
        height: 10,
        width: 10,
    },
    '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track': {
        backgroundColor: '#f1f1f1',
    },
    '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb': {
        backgroundColor: '#c1c1c1',
        borderRadius: 8,
        border: '2px solid #f1f1f1',
    },
    '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb:hover': {
        backgroundColor: '#a0a0a0',
    },
    // Body cells scroll natively, so sticky keeps them on the right edge.
    [`& .MuiDataGrid-cell.${STICKY_CLASS}`]: {
        position: 'sticky',
        right: 0,
        zIndex: 1,
        backgroundColor: '#fff',
        boxShadow: stickyShadow,
    },
    [`& .MuiDataGrid-row:hover .MuiDataGrid-cell.${STICKY_CLASS}`]: {
        backgroundColor: '#f5f5f5',
    },
    // Headers are moved with a transform (not native scroll), so sticky does not
    // apply; the offset is synced from the body scroll position instead.
    [`& .MuiDataGrid-columnHeader.${STICKY_CLASS}`]: {
        transform: `translateX(var(${HEADER_OFFSET_VAR}, 0px))`,
        zIndex: 1,
        backgroundColor: '#fff',
        boxShadow: stickyShadow,
    },
}

const withStickyActions = (columns: readonly GridColDef<any>[]) =>
    columns.map(column =>
        column.field === ACTIONS_FIELD
            ? {
                ...column,
                align: 'center' as const,
                headerAlign: 'center' as const,
                cellClassName: STICKY_CLASS,
                headerClassName: STICKY_CLASS,
            }
            : column
    )

const ScrollableDataGrid = ({ columns, sx, ...props }: DataGridProps) => {

    const wrapperRef = useRef<HTMLDivElement>(null)
    const stickyColumns = useMemo(() => withStickyActions(columns), [columns])
    const [showScrollHint, setShowScrollHint] = useState(false)

    useEffect(() => {
        const wrapper = wrapperRef.current
        if (!wrapper) return

        const updateHeaderOffset = () => {
            const scroller = wrapper.querySelector<HTMLElement>('.MuiDataGrid-virtualScroller')
            if (!scroller) return
            const maxScrollLeft = Math.max(0, scroller.scrollWidth - scroller.clientWidth)
            wrapper.style.setProperty(HEADER_OFFSET_VAR, `${scroller.scrollLeft - maxScrollLeft}px`)

            // An overlay scrollbar (e.g. Firefox on macOS, which ignores ::-webkit-scrollbar)
            // takes no layout space, so it is hidden until the user scrolls.
            const hasOverlayScrollbar = scroller.offsetHeight === scroller.clientHeight
            setShowScrollHint(maxScrollLeft > 0 && hasOverlayScrollbar)
        }

        updateHeaderOffset()

        // Scroll events do not bubble, so listen in the capture phase.
        wrapper.addEventListener('scroll', updateHeaderOffset, true)

        const resizeObserver = new ResizeObserver(updateHeaderOffset)
        resizeObserver.observe(wrapper)
        const content = wrapper.querySelector('.MuiDataGrid-virtualScrollerContent')
        if (content) resizeObserver.observe(content)

        return () => {
            wrapper.removeEventListener('scroll', updateHeaderOffset, true)
            resizeObserver.disconnect()
        }
    }, [stickyColumns])

    return (
        <div ref={wrapperRef} style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
            {showScrollHint && (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 4,
                    marginBottom: 6,
                    fontSize: 12,
                    color: '#626262',
                }}>
                    <SwapHorizIcon style={{ fontSize: 16 }} />
                    Desliza la tabla con Shift + rueda del mouse o con dos dedos en el trackpad
                </div>
            )}
            <div style={{ flex: '1 1 auto', minHeight: 0 }}>
                <DataGrid
                    columns={stickyColumns}
                    columnBuffer={COLUMN_BUFFER}
                    sx={[gridSx, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
                    {...props}
                />
            </div>
        </div>
    )
}

export default ScrollableDataGrid
